import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertCircle, ArrowUpDown, Camera, CheckCircle2, ChevronDown, ChevronLeft, ChevronRight, ChevronUp, Download, FileUp, GripVertical, HelpCircle, Image as ImageIcon, Info, Loader2, Plus, ScanBarcode, Sparkles, Trash2, Upload, X } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/Button';
import { useAuth } from '../../context/AuthContext';
import { useProducts } from '../../context/ProductsContext';
import { getByBarcode, listProducts, lookupBarcodeEnrich, onboardProductsBulk } from '../../api/inventory';
import { GST_RATES } from '../../utils/gst';
import { applyAutoSkus } from '../../utils/sku';
import form from '../../styles/form.module.css';
import styles from './BulkOnboard.module.css';

const UNITS = ['ea', 'kg', 'bag', 'pack', 'bottle', 'pouch', 'tray', 'piece'];
const CSV_HEADERS = ['name', 'sku', 'barcode', 'category', 'brand', 'cost', 'selling_price', 'mrp', 'stock', 'unit', 'gst', 'hsn', 'image_url', 'sold_by'];
const SOLD_BY_OPTIONS = ['EACH', 'WEIGHT'];
const HEADER_ALIASES = {
  name: ['name', 'product', 'product name', 'product_name'],
  sku: ['sku'],
  barcode: ['barcode'],
  category: ['category'],
  brand: ['brand'],
  costPrice: ['cost', 'costprice', 'cost_price', 'cost price'],
  price: ['price', 'selling price', 'selling_price', 'sellingprice'],
  mrp: ['mrp'],
  stock: ['stock', 'qty', 'quantity'],
  unit: ['unit'],
  soldBy: ['sold_by', 'soldby', 'sold as', 'sold_as'],
  gstRate: ['gst', 'gst_rate', 'gstrate', 'gst rate'],
  hsnCode: ['hsn', 'hsncode', 'hsn_code', 'hsn code'],
  imageUrl: ['image_url', 'image url', 'image', 'photo', 'picture', 'image_link', 'imagelink'],
};

let rowIdSeq = 0;
const uid = () => {
  rowIdSeq += 1;
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `bulk-row-${Date.now()}-${rowIdSeq}-${Math.random().toString(36).slice(2, 10)}`;
};

const BARCODE_COL_INDEX = CSV_HEADERS.indexOf('barcode');
// Points — wide enough for a full EAN-13 / GTIN without clipping to scientific notation.
const BARCODE_COL_WIDTH = 168;

function xmlEscape(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function spreadsheetRow(values, { header = false } = {}) {
  const cells = values.map((value, index) => {
    const raw = String(value ?? '');
    const isBarcode = index === BARCODE_COL_INDEX;
    if (!raw) return '<Cell/>';
    const styleId = header
      ? (isBarcode ? 'barcodeHeader' : 'header')
      : (isBarcode ? 'barcode' : '');
    const styleAttr = styleId ? ` ss:StyleID="${styleId}"` : '';
    const asText = header || isBarcode || !/^-?\d+(\.\d+)?$/.test(raw);
    const type = asText ? 'String' : 'Number';
    return `<Cell${styleAttr}><Data ss:Type="${type}">${xmlEscape(raw)}</Data></Cell>`;
  }).join('');
  return `<Row>${cells}</Row>`;
}

function buildOnboardTemplateWorkbook(dataRows) {
  const barcodeCol = BARCODE_COL_INDEX + 1;
  return `<?xml version="1.0" encoding="UTF-8"?>
<?mso-application progid="Excel.Sheet"?>
<Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet"
 xmlns:o="urn:schemas-microsoft-com:office:office"
 xmlns:x="urn:schemas-microsoft-com:office:excel"
 xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet"
 xmlns:html="http://www.w3.org/TR/REC-html40">
 <Styles>
  <Style ss:ID="header">
   <Font ss:Bold="1"/>
  </Style>
  <Style ss:ID="barcode">
   <NumberFormat ss:Format="@"/>
  </Style>
  <Style ss:ID="barcodeHeader">
   <Font ss:Bold="1"/>
   <NumberFormat ss:Format="@"/>
  </Style>
 </Styles>
 <Worksheet ss:Name="Products">
  <Table>
   <Column ss:Index="${barcodeCol}" ss:AutoFitWidth="0" ss:Width="${BARCODE_COL_WIDTH}" ss:StyleID="barcode"/>
   ${spreadsheetRow(CSV_HEADERS, { header: true })}
   ${dataRows.map((row) => spreadsheetRow(row)).join('\n   ')}
  </Table>
 </Worksheet>
</Workbook>`;
}

function countUnquoted(line, delimiter) {
  let count = 0;
  let inQuotes = false;
  for (let i = 0; i < line.length; i += 1) {
    const c = line[i];
    if (c === '"') inQuotes = !inQuotes;
    else if (!inQuotes && c === delimiter) count += 1;
  }
  return count;
}

function detectDelimiter(text) {
  const firstLine = String(text || '').split(/\r?\n/).find((line) => line.trim()) || '';
  if (firstLine.includes('\t')) return '\t';
  const semis = countUnquoted(firstLine, ';');
  const commas = countUnquoted(firstLine, ',');
  return semis > commas ? ';' : ',';
}

function parseCsv(text) {
  const rows = [];
  let row = [];
  let cell = '';
  let inQuotes = false;
  const src = String(text || '').replace(/^\uFEFF/, '');
  const delimiter = detectDelimiter(src);

  for (let i = 0; i < src.length; i += 1) {
    const c = src[i];
    const next = src[i + 1];
    if (inQuotes) {
      if (c === '"' && next === '"') {
        cell += '"';
        i += 1;
      } else if (c === '"') {
        inQuotes = false;
      } else {
        cell += c;
      }
    } else if (c === '"') {
      inQuotes = true;
    } else if (c === delimiter) {
      row.push(cell.trim());
      cell = '';
    } else if (c === '\n') {
      row.push(cell.trim());
      if (row.some(Boolean)) rows.push(row);
      row = [];
      cell = '';
    } else if (c !== '\r') {
      cell += c;
    }
  }
  if (cell || row.length) {
    row.push(cell.trim());
    if (row.some(Boolean)) rows.push(row);
  }
  return rows;
}

function parseHtmlTable(html) {
  if (!html || !/<table/i.test(html)) return [];
  try {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return [...doc.querySelectorAll('tr')]
      .map((tr) => [...tr.querySelectorAll('th,td')].map((td) => td.innerText.replace(/\s+/g, ' ').trim()))
      .filter((row) => row.some(Boolean));
  } catch {
    return [];
  }
}

function isSpreadsheetPaste(text, html) {
  if (parseHtmlTable(html).length) return true;
  const normalized = String(text || '').replace(/^\uFEFF/, '').trim();
  if (!normalized) return false;
  const lines = normalized.split(/\r?\n/).filter(Boolean);
  if (lines.length >= 2) return /[\t,;]/.test(lines[0]);
  return (normalized.match(/\t/g) || []).length >= 2
    || countUnquoted(normalized, ',') >= 3
    || countUnquoted(normalized, ';') >= 3;
}

function parseGstValue(raw) {
  if (raw === '' || raw == null) return null;
  const cleaned = String(raw).trim().replace(/%/g, '').trim();
  if (!cleaned) return null;
  const num = Number(cleaned);
  if (!Number.isFinite(num)) return null;
  return GST_RATES.includes(num) ? num : null;
}

/** Maps BE / local bulk-import error codes to UI copy. */
const BULK_ERROR_MESSAGES = {
  name_required: 'Product name is required.',
  unknown_category: 'Unknown category — pick an active category.',
  invalid_hsn: 'HSN must be 4–8 digits.',
  hsn_required: 'HSN code is required.',
  invalid_gst_rate: `GST % must be ${GST_RATES.join(', ')}.`,
  gst_rate_required: 'GST rate is required.',
  invalid_sold_by: 'Sold as must be Pack or Loose.',
  invalid_qty_rules: 'Quantity rules are invalid.',
  max_order_qty_below_min: 'Max order qty must be ≥ min order qty.',
  already_exists: 'Product already exists (skipped).',
  barcode_taken: 'Barcode is already used by another product.',
  duplicate_sku: 'SKU is used more than once in this batch.',
  duplicate_barcode: 'Barcode is used more than once in this batch.',
  image_import_failed: 'Image import failed.',
  stock_in_failed: 'Stock-in failed after create.',
  row_error: 'This row failed to import.',
};

function formatBulkError(code) {
  if (!code) return '';
  const key = String(code).trim();
  if (BULK_ERROR_MESSAGES[key]) return BULK_ERROR_MESSAGES[key];
  // Backend sometimes joins multiple codes with "; "
  if (key.includes(';')) {
    return key.split(';').map((part) => formatBulkError(part.trim())).filter(Boolean).join('; ');
  }
  return key;
}

function isValidHsn(value) {
  return /^\d{4,8}$/.test(String(value || '').trim());
}

/** In-batch SKU/barcode clashes — BE dry-run does not simulate these. */
function findInBatchConflicts(rowList) {
  const skuOwners = new Map();
  const barcodeOwners = new Map();
  const errors = {};

  const mark = (rowId, field) => {
    const code = field === 'sku' ? 'duplicate_sku' : 'duplicate_barcode';
    const prev = errors[rowId];
    const fields = new Set(prev?.fields || []);
    fields.add(field);
    const codes = new Set(String(prev?.error || '').split(';').map((part) => part.trim()).filter(Boolean));
    codes.add(code);
    errors[rowId] = {
      ok: false,
      status: 'error',
      error: [...codes].join('; '),
      fields: [...fields],
    };
  };

  rowList.forEach((row) => {
    const skuKey = String(row.sku || '').trim().toLowerCase();
    if (skuKey) {
      if (skuOwners.has(skuKey)) {
        mark(row.id, 'sku');
        mark(skuOwners.get(skuKey), 'sku');
      } else {
        skuOwners.set(skuKey, row.id);
      }
    }

    const barcodeKey = String(row.barcode || '').trim();
    if (barcodeKey) {
      if (barcodeOwners.has(barcodeKey)) {
        mark(row.id, 'barcode');
        mark(barcodeOwners.get(barcodeKey), 'barcode');
      } else {
        barcodeOwners.set(barcodeKey, row.id);
      }
    }
  });

  return errors;
}

/** Direct catalog barcode check via DynamoDB barcode registry. */
async function findCatalogBarcodeConflicts(rowList, token) {
  if (!token) return {};
  const barcodeMap = new Map();
  rowList.forEach((row) => {
    const barcodeKey = String(row.barcode || '').trim();
    if (barcodeKey) {
      if (!barcodeMap.has(barcodeKey)) barcodeMap.set(barcodeKey, []);
      barcodeMap.get(barcodeKey).push(row.id);
    }
  });

  if (barcodeMap.size === 0) return {};

  const errors = {};
  const checks = Array.from(barcodeMap.entries()).map(async ([code, rowIds]) => {
    try {
      const existing = await getByBarcode(code, token);
      if (existing) {
        const label = existing.name || existing.sku || code;
        rowIds.forEach((rowId) => {
          errors[rowId] = {
            ok: false,
            status: 'error',
            error: `Barcode is already used by "${label}".`,
            fields: ['barcode'],
          };
        });
      }
    } catch {
      // In case of unexpected network failure, dry-run will handle it.
    }
  });

  await Promise.all(checks);
  return errors;
}

function duplicateFields(result) {
  const fields = new Set(Array.isArray(result?.fields) ? result.fields : []);
  const text = String(result?.error || '').toLowerCase();
  if (text.includes('sku')) fields.add('sku');
  if (text.includes('barcode')) fields.add('barcode');
  return fields;
}

function headerIndexMap(headerRow) {
  const normalized = headerRow.map((h) => h.toLowerCase().trim());
  const map = {};
  Object.entries(HEADER_ALIASES).forEach(([field, aliases]) => {
    const idx = normalized.findIndex((h) => aliases.includes(h));
    if (idx >= 0) map[field] = idx;
  });
  return map;
}

const emptyRow = (categoryId = '', gstRate = GST_RATES[0]) => ({
  id: uid(),
  name: '',
  sku: '',
  skuAuto: false,
  barcode: '',
  category: categoryId,
  brand: '',
  costPrice: '',
  price: '',
  mrp: '',
  stock: '',
  unit: UNITS[0],
  soldBy: 'EACH',
  gstRate,
  hsnCode: '',
  imageUrl: '',
  localFiles: [],
});

function getRowLocalFiles(row) {
  if (row.localFiles?.length) return row.localFiles;
  if (row.localFile) return [row.localFile];
  return [];
}

function isBlankRow(row) {
  return !row.name.trim() && !row.sku.trim() && !row.barcode.trim();
}

function matchCategoryId(value, activeCategories, defaultCategoryId) {
  const needle = (value || '').trim().toLowerCase();
  if (!needle) return defaultCategoryId;
  const byId = activeCategories.find((c) => c.id.toLowerCase() === needle);
  if (byId) return byId.id;
  const byName = activeCategories.find((c) => c.name.toLowerCase() === needle);
  return byName?.id || defaultCategoryId;
}

function normalizeGstRate(rate) {
  const num = Number(rate);
  return GST_RATES.includes(num) ? num : GST_RATES[0];
}

function formatEnrichSource(source) {
  if (!source || source === 'catalog') return 'catalog';
  return String(source).replace(/_/g, ' ');
}


function rowFromEnrich(suggestion, barcode, activeCategories, defaultCategoryId, defaultGst) {
  const categoryId = matchCategoryId(suggestion?.category, activeCategories, defaultCategoryId);
  const cat = activeCategories.find((c) => c.id === categoryId);
  const gstRate = normalizeGstRate(cat?.defaultGstRate ?? defaultGst);
  return {
    ...emptyRow(categoryId, gstRate),
    barcode,
    name: suggestion?.name || '',
    brand: suggestion?.brand || '',
    imageUrl: suggestion?.image_url || '',
  };
}

function SortableImageThumb({ file }) {
  const [previewUrl, setPreviewUrl] = useState('');

  useEffect(() => {
    const objectUrl = URL.createObjectURL(file);
    setPreviewUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [file]);

  if (!previewUrl) return <div className={styles.sortThumbPlaceholder} />;

  return (
    <img
      src={previewUrl}
      alt={file.name || 'preview'}
      className={styles.sortThumb}
    />
  );
}

function ImageOrderModal({ rowLabel, files, onSave, onClose, disabled }) {
  const [orderedFiles, setOrderedFiles] = useState(files);
  const [dragIdx, setDragIdx] = useState(null);

  useEffect(() => {
    setOrderedFiles(files);
  }, [files]);

  const moveFile = (fromIdx, toIdx) => {
    if (toIdx < 0 || toIdx >= orderedFiles.length || fromIdx === toIdx) return;
    setOrderedFiles((prev) => {
      const next = [...prev];
      const [item] = next.splice(fromIdx, 1);
      next.splice(toIdx, 0, item);
      return next;
    });
  };

  const handleDragStart = (idx) => {
    if (disabled) return;
    setDragIdx(idx);
  };

  const handleDragOver = (e, idx) => {
    e.preventDefault();
    if (disabled || dragIdx === null || dragIdx === idx) return;
    moveFile(dragIdx, idx);
    setDragIdx(idx);
  };

  const handleDragEnd = () => setDragIdx(null);

  const orderChanged = orderedFiles.some((file, idx) => file !== files[idx]);

  const sortByFilename = () => {
    setOrderedFiles((prev) => [...prev].sort((a, b) => (
      (a.name || '').localeCompare(b.name || '', undefined, { numeric: true, sensitivity: 'base' })
    )));
  };

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modalCard} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <div className={styles.modalTitleWrap}>
            <ImageIcon className={styles.modalTitleIcon} size={18} />
            <h3 className={styles.modalTitle}>Image order</h3>
          </div>
          <button
            type="button"
            className={styles.modalCloseBtn}
            onClick={onClose}
            aria-label="Close modal"
          >
            <X size={16} />
          </button>
        </div>
        <div className={styles.modalBody}>
          <p className={styles.imageOrderHint}>
            {rowLabel ? `"${rowLabel}" — ` : ''}
            Drag images or use arrows to set display order. The first image is used as the cover photo.
          </p>
          <ol className={styles.imageOrderList}>
            {orderedFiles.map((file, idx) => (
              <li
                key={`${file.name}-${file.size}-${file.lastModified}-${idx}`}
                className={`${styles.imageOrderItem} ${dragIdx === idx ? styles.imageOrderItemDragging : ''}`}
                draggable={!disabled}
                onDragStart={() => handleDragStart(idx)}
                onDragOver={(e) => handleDragOver(e, idx)}
                onDragEnd={handleDragEnd}
              >
                <span className={styles.imageOrderGrip} aria-hidden="true">
                  <GripVertical size={14} />
                </span>
                <span className={styles.imageOrderRank}>{idx + 1}</span>
                <SortableImageThumb file={file} />
                <span className={styles.imageOrderName} title={file.name}>
                  {file.name || `Image ${idx + 1}`}
                </span>
                {idx === 0 && <span className={styles.imageOrderCoverBadge}>Cover</span>}
                <div className={styles.imageOrderActions}>
                  <button
                    type="button"
                    className={styles.imageOrderMoveBtn}
                    onClick={() => moveFile(idx, idx - 1)}
                    disabled={disabled || idx === 0}
                    aria-label={`Move image ${idx + 1} up`}
                  >
                    <ChevronUp size={14} />
                  </button>
                  <button
                    type="button"
                    className={styles.imageOrderMoveBtn}
                    onClick={() => moveFile(idx, idx + 1)}
                    disabled={disabled || idx === orderedFiles.length - 1}
                    aria-label={`Move image ${idx + 1} down`}
                  >
                    <ChevronDown size={14} />
                  </button>
                </div>
              </li>
            ))}
          </ol>
        </div>
        <div className={styles.modalFooter} style={{ gap: 8 }}>
          <Button variant="outline" onClick={sortByFilename} disabled={disabled}>Sort 1, 2, 3...</Button>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            onClick={() => onSave(orderedFiles)}
            disabled={disabled || !orderChanged}
          >
            Save order
          </Button>
        </div>
      </div>
    </div>
  );
}

function RowImagePreview({ file, index, totalFiles, onMoveLeft, onMoveRight, onOpenOrderModal, onRemove, disabled }) {
  const [previewUrl, setPreviewUrl] = useState('');

  useEffect(() => {
    const objectUrl = URL.createObjectURL(file);
    setPreviewUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [file]);

  if (!previewUrl) return null;

  return (
    <div className={styles.imagePreviewWrap}>
      <img
        src={previewUrl}
        alt={file.name || 'preview'}
        className={styles.imageThumb}
        onClick={totalFiles > 1 ? onOpenOrderModal : undefined}
        title={totalFiles > 1 ? 'Click to open image order window' : undefined}
        style={{ cursor: totalFiles > 1 ? 'pointer' : 'default' }}
      />
      {totalFiles > 1 && !disabled && (
        <div className={styles.thumbMoveControls}>
          <button
            type="button"
            className={styles.moveThumbBtn}
            onClick={(e) => { e.stopPropagation(); onMoveLeft(); }}
            disabled={index === 0}
            title="Move image left"
            aria-label="Move image left"
          >
            <ChevronLeft size={11} />
          </button>
          <button
            type="button"
            className={styles.moveThumbBtn}
            onClick={(e) => { e.stopPropagation(); onMoveRight(); }}
            disabled={index === totalFiles - 1}
            title="Move image right"
            aria-label="Move image right"
          >
            <ChevronRight size={11} />
          </button>
        </div>
      )}
      <button
        type="button"
        className={styles.clearImageBtn}
        onClick={(e) => { e.stopPropagation(); onRemove(); }}
        title="Remove image"
        disabled={disabled}
      >
        <X size={11} />
      </button>
    </div>
  );
}

function RowStatus({ result }) {
  if (!result) return null;
  if (result.status === 'pending') {
    return <span className={`${styles.rowStatus} ${styles.rowPending}`}><Info size={13} /> Queued</span>;
  }
  if (result.status === 'validating') {
    return <span className={`${styles.rowStatus} ${styles.rowPending}`}><Info size={13} /> Validating...</span>;
  }
  if (result.status === 'validated') {
    return <span className={`${styles.rowStatus} ${styles.rowValidated}`}><CheckCircle2 size={13} /> Validated</span>;
  }
  if (result.status === 'uploading_image') {
    return <span className={`${styles.rowStatus} ${styles.rowUploading}`}><Upload size={13} /> Uploading image...</span>;
  }
  if (result.status === 'success') {
    return <span className={`${styles.rowStatus} ${styles.rowSuccess}`}><CheckCircle2 size={13} /> Onboarded</span>;
  }
  if (result.status === 'error') {
    return (
      <span className={`${styles.rowStatus} ${styles.rowError}`} title={result.error}>
        <AlertCircle size={13} /> Failed
      </span>
    );
  }
  return null;
}

function looksLikeHeader(cells) {
  const first = (cells[0] || '').toLowerCase();
  if (first === 'name' || first === 'product' || first === 'product name') return true;
  return Object.keys(headerIndexMap(cells)).length >= 3;
}

export default function BulkOnboard() {
  const { user } = useAuth();
  const { categories, products, reloadProducts, mergeProducts } = useProducts();
  const activeCategories = useMemo(
    () => categories
      .filter((c) => c.status === 'ACTIVE')
      .sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' })),
    [categories],
  );
  const defaultCategoryId = activeCategories[0]?.id || '';
  const defaultGst = activeCategories[0]?.defaultGstRate ?? GST_RATES[0];

  const [rows, setRows] = useState(() => [emptyRow()]);
  const [rowResults, setRowResults] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [validating, setValidating] = useState(false);
  const [globalError, setGlobalError] = useState('');
  const [validatedNotice, setValidatedNotice] = useState('');
  const [batchValidated, setBatchValidated] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [showValidateInfoModal, setShowValidateInfoModal] = useState(false);
  const [imageOrderRowId, setImageOrderRowId] = useState(null);
  const [scanValue, setScanValue] = useState('');
  const [scanning, setScanning] = useState(false);
  const [scanNotice, setScanNotice] = useState(null);
  const [autoGenerateSku, setAutoGenerateSku] = useState(false);

  const fileInputRef = useRef(null);
  const pageRef = useRef(null);
  const pasteAreaRef = useRef(null);
  const ingestRef = useRef(() => {});
  const scanRef = useRef(null);
  const scanQueueRef = useRef([]);
  const processingScanRef = useRef(false);
  const autoGenerateSkuRef = useRef(false);
  const productsRef = useRef(products);
  const categoriesRef = useRef(activeCategories);

  autoGenerateSkuRef.current = autoGenerateSku;
  productsRef.current = products;
  categoriesRef.current = activeCategories;

  const locked = submitting || validating || submitted;

  const withAutoSkus = (rowList) => {
    if (!autoGenerateSkuRef.current) return rowList;
    return applyAutoSkus(rowList, productsRef.current, categoriesRef.current);
  };

  const focusScanField = () => {
    scanRef.current?.focus({ preventScroll: true });
  };

  useEffect(() => {
    if (!submitted && !locked) focusScanField();
  }, [submitted, locked]);

  useEffect(() => {
    if (!defaultCategoryId) return;
    setRows((prev) => {
      let changed = false;
      const next = prev.map((r) => {
        if (r.category) return r;
        changed = true;
        return {
          ...r,
          category: defaultCategoryId,
          gstRate: normalizeGstRate(parseGstValue(r.gstRate) ?? defaultGst),
        };
      });
      return changed ? next : prev;
    });
  }, [defaultCategoryId, defaultGst]);

  useEffect(() => {
    setBatchValidated(false);
    setValidatedNotice('');
  }, [rows]);

  const reset = () => {
    setRows([emptyRow(defaultCategoryId, defaultGst)]);
    setRowResults({});
    setGlobalError('');
    setValidatedNotice('');
    setBatchValidated(false);
    setSubmitted(false);
    setScanValue('');
    setScanNotice(null);
  };

  const insertScannedRow = (rowData) => {
    setRows((prev) => {
      const next = prev.length === 1 && isBlankRow(prev[0]) ? [rowData] : [...prev, rowData];
      return withAutoSkus(next);
    });
  };

  const applyEnrichToRow = (rowId, suggestion, barcode) => {
    setRows((prev) => {
      const next = prev.map((r) => {
        if (r.id !== rowId) return r;
        const enriched = rowFromEnrich(suggestion, barcode, activeCategories, defaultCategoryId, defaultGst);
        // Keep a manually entered SKU on the target row when enriching.
        if (String(r.sku || '').trim() && !r.skuAuto) {
          return { ...enriched, id: rowId, sku: r.sku, skuAuto: false };
        }
        return { ...enriched, id: rowId, sku: '', skuAuto: false };
      });
      return withAutoSkus(next);
    });
  };

  const resolveScannedBarcode = async ({ code, targetRowId = null }, seenBarcodes) => {
    if (!user?.idToken) return;
    if (!/^\d+$/.test(code)) {
      setScanNotice({ tone: 'warning', text: 'Barcode must be digits only (EAN-13 / UPC).' });
      return;
    }

    if (seenBarcodes.has(code)) {
      setScanNotice({ tone: 'warning', text: `Barcode ${code} is already in this batch.` });
      return;
    }

    try {
      // Direct catalog check first via DynamoDB barcode registry
      const catalogItem = await getByBarcode(code, user.idToken);
      if (catalogItem) {
        setScanNotice({
          tone: 'warning',
          text: `Already onboarded: ${catalogItem.name || code}. `,
          itemId: catalogItem.id || catalogItem.itemId,
        });
        return;
      }

      const data = await lookupBarcodeEnrich(code, user.idToken);

      if (data.already_onboarded && data.item_id) {
        const existing = products.find((p) => p.id === data.item_id);
        const label = existing?.name || data.suggestion?.name || code;
        setScanNotice({
          tone: 'warning',
          text: `Already onboarded: ${label}. `,
          itemId: data.item_id,
        });
        return;
      }

      if (data.found && data.suggestion) {
        if (targetRowId) {
          applyEnrichToRow(targetRowId, data.suggestion, code);
        } else {
          insertScannedRow(rowFromEnrich(data.suggestion, code, activeCategories, defaultCategoryId, defaultGst));
        }
        seenBarcodes.add(code);
        setScanNotice({
          tone: 'success',
          text: autoGenerateSkuRef.current
            ? `Prefilled from ${formatEnrichSource(data.source)}. SKU auto-filled where empty — check HSN, GST, and prices.`
            : `Prefilled from ${formatEnrichSource(data.source)}. Enter SKU, HSN, GST, and prices.`,
        });
        return;
      }

      const manualRow = { ...emptyRow(defaultCategoryId, defaultGst), barcode: code };
      if (targetRowId) {
        setRows((prev) => withAutoSkus(prev.map((r) => (r.id === targetRowId ? { ...r, barcode: code } : r))));
      } else {
        insertScannedRow(manualRow);
      }
      seenBarcodes.add(code);
      setScanNotice({
        tone: 'info',
        text: 'Not in external databases — enter product details manually.',
      });
    } catch (err) {
      if (err.message === 'forbidden') {
        setScanNotice(null);
        return;
      }
      if (err.message === 'invalid_barcode' || err.message === 'barcode_required') {
        setScanNotice({ tone: 'warning', text: 'Invalid barcode — use digits only.' });
        return;
      }
      if (targetRowId) {
        setRows((prev) => withAutoSkus(prev.map((r) => (r.id === targetRowId ? { ...r, barcode: code } : r))));
      } else {
        insertScannedRow({ ...emptyRow(defaultCategoryId, defaultGst), barcode: code });
      }
      seenBarcodes.add(code);
      setScanNotice({
        tone: 'info',
        text: err.name === 'AbortError' || err.message === 'timeout'
          ? 'Lookup timed out — enter product details manually.'
          : 'Could not enrich barcode — enter product details manually.',
      });
    }
  };

  const drainScanQueue = async () => {
    if (processingScanRef.current) return;
    processingScanRef.current = true;
    setScanning(true);
    setScanNotice(null);
    const seenBarcodes = new Set(rows.map((r) => r.barcode).filter(Boolean));
    try {
      while (scanQueueRef.current.length > 0) {
        const item = scanQueueRef.current.shift();
        if (item.targetRowId) {
          const target = rows.find((r) => r.id === item.targetRowId);
          if (target?.barcode) seenBarcodes.delete(target.barcode);
        }
        // eslint-disable-next-line no-await-in-loop
        await resolveScannedBarcode(item, seenBarcodes);
      }
    } finally {
      processingScanRef.current = false;
      setScanning(false);
      setScanValue('');
      requestAnimationFrame(() => focusScanField());
    }
  };

  const handleScanKeyDown = (e) => {
    if (e.key !== 'Enter') return;
    const code = scanValue.trim();
    if (!code) return;
    e.preventDefault();
    setScanValue('');
    scanQueueRef.current.push({ code });
    drainScanQueue();
  };

  const handleRowBarcodeKeyDown = (e, rowId) => {
    if (e.key !== 'Enter') return;
    const code = e.currentTarget.value.trim();
    if (!code) return;
    e.preventDefault();
    updateRow(rowId, 'barcode', code);
    scanQueueRef.current.push({ code, targetRowId: rowId });
    drainScanQueue();
  };

  const updateRow = (id, field, value) => {
    setRows((prev) => {
      const next = prev.map((r) => {
        if (r.id !== id) return r;
        if (field === 'sku') {
          return { ...r, sku: value, skuAuto: false };
        }
        if (field === 'category') {
          const cat = activeCategories.find((c) => c.id === value);
          return {
            ...r,
            category: value,
            gstRate: normalizeGstRate(cat?.defaultGstRate ?? r.gstRate),
          };
        }
        if (field === 'soldBy' && value === 'WEIGHT' && (r.unit === 'ea' || r.unit === 'piece')) {
          return { ...r, soldBy: value, unit: 'kg' };
        }
        return { ...r, [field]: value };
      });

      const shouldApplySku = autoGenerateSkuRef.current && (
        field === 'name'
        || field === 'category'
        || field === 'barcode'
        || (field === 'sku' && !String(value || '').trim())
      );
      return shouldApplySku ? withAutoSkus(next) : next;
    });
  };

  const handleRowFilesChange = (id, newFiles) => {
    if (!newFiles || !newFiles.length) return;
    const incoming = Array.from(newFiles);
    setRows((prev) => prev.map((r) => {
      if (r.id !== id) return r;
      const current = r.localFiles || (r.localFile ? [r.localFile] : []);
      const combined = [...current, ...incoming]
        .sort((a, b) => (
          (a.name || '').localeCompare(b.name || '', undefined, { numeric: true, sensitivity: 'base' })
        ))
        .slice(0, 5);
      return { ...r, localFiles: combined, localFile: combined[0] || null };
    }));
  };

  const removeRowImage = (id, indexToRemove) => {
    setRows((prev) => prev.map((r) => {
      if (r.id !== id) return r;
      const current = getRowLocalFiles(r);
      const updated = current.filter((_, idx) => idx !== indexToRemove);
      return { ...r, localFiles: updated, localFile: updated[0] || null };
    }));
  };

  const moveRowImage = (id, fromIdx, toIdx) => {
    setRows((prev) => prev.map((r) => {
      if (r.id !== id) return r;
      const current = getRowLocalFiles(r);
      if (toIdx < 0 || toIdx >= current.length || fromIdx === toIdx) return r;
      const next = [...current];
      const [moved] = next.splice(fromIdx, 1);
      next.splice(toIdx, 0, moved);
      return { ...r, localFiles: next, localFile: next[0] || null };
    }));
  };

  const reorderRowImages = (id, orderedFiles) => {
    setRows((prev) => prev.map((r) => (
      r.id === id
        ? { ...r, localFiles: orderedFiles, localFile: orderedFiles[0] || null }
        : r
    )));
    setImageOrderRowId(null);
  };

  const addRow = () => {
    setRows((prev) => [...prev, emptyRow(defaultCategoryId, defaultGst)]);
  };

  const handleToggleAutoGenerateSku = () => {
    if (locked) return;
    if (autoGenerateSku) {
      setAutoGenerateSku(false);
      return;
    }
    setAutoGenerateSku(true);
    setRows((prev) => applyAutoSkus(prev, products, activeCategories));
  };

  const removeRow = (id) => {
    setRows((prev) => (prev.length === 1 ? prev : prev.filter((r) => r.id !== id)));
  };

  const matchCategory = (value) => {
    const needle = (value || '').trim().toLowerCase();
    if (!needle) return defaultCategoryId;
    const byId = activeCategories.find((c) => c.id.toLowerCase() === needle);
    if (byId) return byId.id;
    const byName = activeCategories.find((c) => c.name.toLowerCase() === needle);
    return byName?.id || defaultCategoryId;
  };

  const matchUnit = (value) => {
    const needle = (value || '').trim().toLowerCase();
    return UNITS.includes(needle) ? needle : UNITS[0];
  };

  const matchSoldBy = (value) => {
    const needle = String(value || '').trim().toUpperCase();
    if (!needle) return 'EACH';
    return SOLD_BY_OPTIONS.includes(needle) ? needle : needle;
  };

  const rowsFromTable = (table) => {
    if (!table.length) return [];
    let start = 0;
    let col = (cells, field, fallbackIndex) => cells[fallbackIndex] || '';
    if (looksLikeHeader(table[0])) {
      const map = headerIndexMap(table[0]);
      col = (cells, field, fallbackIndex) => cells[map[field] ?? fallbackIndex] || '';
      start = 1;
    }

    return table.slice(start).map((cells) => {
      const category = col(cells, 'category', 3);
      const gstRaw = col(cells, 'gstRate', 10);
      const categoryId = matchCategory(category);
      const cat = activeCategories.find((c) => c.id === categoryId);
      const parsedGst = parseGstValue(gstRaw);
      return {
        id: uid(),
        name: col(cells, 'name', 0),
        sku: col(cells, 'sku', 1),
        skuAuto: false,
        barcode: col(cells, 'barcode', 2),
        category: categoryId,
        brand: col(cells, 'brand', 4),
        costPrice: col(cells, 'costPrice', 5),
        price: col(cells, 'price', 6),
        mrp: col(cells, 'mrp', 7),
        stock: col(cells, 'stock', 8),
        unit: matchUnit(col(cells, 'unit', 9)),
        soldBy: matchSoldBy(col(cells, 'soldBy', 13)),
        gstRate: normalizeGstRate(parsedGst ?? cat?.defaultGstRate ?? defaultGst),
        hsnCode: col(cells, 'hsnCode', 11),
        imageUrl: col(cells, 'imageUrl', 12),
        localFiles: [],
        localFile: null,
      };
    }).filter((r) => r.name || r.sku || r.barcode);
  };

  const applyTable = (table) => {
    const parsed = rowsFromTable(table);
    if (!parsed.length) {
      setGlobalError('No product rows found in that CSV. Use the template headers: name, sku, barcode, category, brand, cost, selling_price, mrp, stock, unit, gst, hsn, image_url, sold_by.');
      return false;
    }
    setGlobalError('');
    setValidatedNotice('');
    setRowResults({});
    setSubmitted(false);
    setRows(withAutoSkus(parsed));
    return true;
  };

  const ingestClipboard = (text, html) => {
    const csvRows = parseCsv(text);
    if (csvRows.length) return applyTable(csvRows);
    return applyTable(parseHtmlTable(html));
  };

  ingestRef.current = ingestClipboard;

  useEffect(() => {
    if (locked) return undefined;
    const onPaste = (e) => {
      const target = e.target;
      const inPage = pageRef.current?.contains(target);
      const inChrome = target === document.body || target === document.documentElement;
      if (!inPage && !inChrome) return;
      const text = e.clipboardData?.getData('text/plain') || e.clipboardData?.getData('text') || '';
      const html = e.clipboardData?.getData('text/html') || '';
      if (!isSpreadsheetPaste(text, html)) return;
      e.preventDefault();
      ingestRef.current(text, html);
    };
    window.addEventListener('paste', onPaste);
    return () => window.removeEventListener('paste', onPaste);
  }, [locked]);

  const handlePasteAreaPaste = (e) => {
    const text = e.clipboardData?.getData('text/plain') || e.clipboardData?.getData('text') || '';
    const html = e.clipboardData?.getData('text/html') || '';
    if (!text && !html) return;
    e.preventDefault();
    ingestClipboard(text, html);
    if (pasteAreaRef.current) pasteAreaRef.current.value = '';
  };

  const handlePasteAreaChange = (e) => {
    const value = e.target.value;
    if (!value.trim()) return;
    applyTable(parseCsv(value));
    e.target.value = '';
  };

  const downloadTemplate = () => {
    const categoryName = activeCategories[0]?.name || 'Staples';
    const example = [
      'Aashirvaad Atta 5kg', 'AASH-ATTA-5KG', '8901234567890', categoryName,
      'Aashirvaad', '220', '265', '280', '40', 'kg', '5', '1006',
      'https://images.unsplash.com/photo-1586201375761-83865001e31c', 'EACH',
    ];
    const tomato = [
      'Tomato loose', 'TOM-LOOSE', '', categoryName,
      '', '20', '40', '45', '25.5', 'kg', '0', '070200',
      '', 'WEIGHT',
    ];
    const blank = CSV_HEADERS.map(() => '');
    const workbook = buildOnboardTemplateWorkbook([example, tomato, blank]);
    const blob = new Blob([`\uFEFF${workbook}`], { type: 'application/vnd.ms-excel;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'bhima-product-onboard-template.xls';
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  const handleCsvFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => applyTable(parseCsv(String(reader.result || '')));
    reader.onerror = () => setGlobalError('Could not read that CSV file.');
    reader.readAsText(file);
  };

  const validate = () => {
    for (let i = 0; i < rows.length; i += 1) {
      const r = rows[i];
      const label = r.name || r.sku || `row ${i + 1}`;
      if (!r.name.trim()) return `Row ${i + 1}: product name is required.`;
      if (!r.sku.trim()) return `"${label}": SKU is required.`;
      if (r.price === '' || Number.isNaN(Number(r.price))) return `"${label}": selling price is required.`;
      if (!r.category) return `"${label}": pick a category.`;
      if (!r.hsnCode.trim()) return `"${label}": HSN code is required.`;
      if (!isValidHsn(r.hsnCode)) return `"${label}": HSN must be 4–8 digits.`;
      if (!GST_RATES.includes(Number(r.gstRate))) {
        return `"${label}": GST % must be ${GST_RATES.join(', ')}.`;
      }
      if (r.soldBy && !SOLD_BY_OPTIONS.includes(String(r.soldBy).toUpperCase())) {
        return `"${label}": sold_by must be EACH or WEIGHT.`;
      }
    }
    return null;
  };

  const toPayload = (row) => ({
    name: row.name.trim(),
    sku: row.sku.trim(),
    barcode: row.barcode.trim() || undefined,
    barcodeType: 'EAN13',
    category: row.category,
    brand: row.brand.trim() || undefined,
    costPrice: row.costPrice === '' ? undefined : Number(row.costPrice),
    price: Number(row.price) || 0,
    mrp: row.mrp === '' ? Number(row.price) || 0 : Number(row.mrp),
    stock: row.stock === '' ? 0 : Number(row.stock),
    unit: row.unit,
    soldBy: String(row.soldBy || 'EACH').toUpperCase() === 'WEIGHT' ? 'WEIGHT' : 'EACH',
    gstRate: normalizeGstRate(row.gstRate),
    hsnCode: row.hsnCode.trim(),
    lowStockThreshold: 10,
    minOrderQty: String(row.soldBy || '').toUpperCase() === 'WEIGHT' ? 0.001 : 1,
    maxOrderQty: 10,
    qtyStep: String(row.soldBy || '').toUpperCase() === 'WEIGHT' ? 0.001 : 1,
  });

  const handleValidateDryRun = async () => {
    setGlobalError('');
    setValidatedNotice('');
    setBatchValidated(false);
    setRowResults({});
    const err = validate();
    if (err) {
      setGlobalError(err);
      return;
    }

    // BE dry-run does not simulate in-batch SKU/barcode collisions.
    const conflicts = findInBatchConflicts(rows);
    if (Object.keys(conflicts).length > 0) {
      setRowResults(conflicts);
      setBatchValidated(false);
      setGlobalError('Duplicate SKU or barcode in this batch. Fix the highlighted rows, then validate again.');
      return;
    }

    setValidating(true);
    const initial = {};
    rows.forEach((r) => { initial[r.id] = { status: 'validating' }; });
    setRowResults(initial);

    // Direct catalog barcode check via getByBarcode
    const catalogBarcodeConflicts = await findCatalogBarcodeConflicts(rows, user?.idToken);
    if (Object.keys(catalogBarcodeConflicts).length > 0) {
      setRowResults(catalogBarcodeConflicts);
      setValidating(false);
      setBatchValidated(false);
      const count = Object.keys(catalogBarcodeConflicts).length;
      setGlobalError(`Barcode conflict: ${count} product(s) use barcodes already in the catalog. Fix the highlighted rows, then validate again.`);
      return;
    }

    const items = rows.map((row) => ({
      ...toPayload(row),
      id: row.id,
      imageUrl: row.imageUrl ? row.imageUrl.trim() : undefined,
      localFiles: row.localFiles?.length ? row.localFiles.slice(0, 5) : (row.localFile ? [row.localFile] : undefined),
    }));

    const results = await onboardProductsBulk(items, user.idToken, {
      dryRun: true,
      onProgress: (item, result) => {
        setRowResults((prev) => ({
          ...prev,
          [item.id]: {
            ...result,
            error: result.error ? formatBulkError(result.error) : result.error,
          },
        }));
      },
    });

    setValidating(false);
    const failCount = results.filter((r) => !r.result.ok).length;
    if (failCount === 0) {
      setBatchValidated(true);
      setValidatedNotice(`Dry-run validation successful! All ${rows.length} product(s) are valid and ready to onboard.`);
    } else {
      setBatchValidated(false);
      setGlobalError(`Dry-run found validation errors on ${failCount} product(s). Please review and correct the errors below.`);
    }
  };

  const handleSubmit = async () => {
    setGlobalError('');
    if (!batchValidated) {
      setGlobalError('Validate Batch (Dry Run) first, then onboard.');
      return;
    }
    setValidatedNotice('');
    const err = validate();
    if (err) {
      setValidatedNotice('');
      setBatchValidated(false);
      setGlobalError(err);
      return;
    }

    const conflicts = findInBatchConflicts(rows);
    if (Object.keys(conflicts).length > 0) {
      setRowResults(conflicts);
      setBatchValidated(false);
      setGlobalError('Duplicate SKU or barcode in this batch. Fix the highlighted rows, then validate again.');
      return;
    }

    // Direct catalog barcode check
    const catalogBarcodeConflicts = await findCatalogBarcodeConflicts(rows, user?.idToken);
    if (Object.keys(catalogBarcodeConflicts).length > 0) {
      setRowResults(catalogBarcodeConflicts);
      setBatchValidated(false);
      const count = Object.keys(catalogBarcodeConflicts).length;
      setGlobalError(`Barcode conflict: ${count} product(s) use barcodes already in the catalog. Fix the highlighted rows, then validate again.`);
      return;
    }

    setSubmitting(true);
    const initial = {};
    rows.forEach((r) => { initial[r.id] = { status: 'pending' }; });
    setRowResults(initial);

    const items = rows.map((row) => ({
      ...toPayload(row),
      id: row.id,
      imageUrl: row.imageUrl ? row.imageUrl.trim() : undefined,
      localFiles: row.localFiles?.length ? row.localFiles.slice(0, 5) : (row.localFile ? [row.localFile] : undefined),
    }));

    try {
      const results = await onboardProductsBulk(items, user.idToken, {
        dryRun: false,
        onProgress: (item, result) => {
          setRowResults((prev) => ({
            ...prev,
            [item.id]: {
              ...result,
              error: result.error ? formatBulkError(result.error) : result.error,
            },
          }));
        },
      });

      const list = await reloadProducts();
      const foundSkus = new Set((list || []).map((p) => String(p.sku || '').trim().toUpperCase()));
      const recovered = [];
      const successes = results.filter((r) => r.result?.ok);
      for (const { item } of successes) {
        const sku = String(item.sku || '').trim().toUpperCase();
        if (sku && foundSkus.has(sku)) continue;
        try {
          if (item.barcode) {
            // eslint-disable-next-line no-await-in-loop
            const found = await getByBarcode(item.barcode, user.idToken);
            if (found) recovered.push(found.item || found);
          } else if (item.sku) {
            // eslint-disable-next-line no-await-in-loop
            const { items: hits } = await listProducts(user.idToken, {
              q: item.sku, limit: 20, includeInactive: true,
            });
            const found = hits.find((p) => String(p.sku || '').trim().toUpperCase() === sku);
            if (found) recovered.push(found);
          }
        } catch {
          // List/barcode lookup is best-effort; the bulk row already reported success.
        }
      }
      if (recovered.length) mergeProducts(recovered);
    } finally {
      setSubmitting(false);
      setSubmitted(true);
    }
  };

  const successCount = Object.values(rowResults).filter((s) => s.status === 'success').length;
  const errorCount = Object.values(rowResults).filter((s) => s.status === 'error').length;
  const imageOrderRow = imageOrderRowId ? rows.find((r) => r.id === imageOrderRowId) : null;
  const imageOrderFiles = imageOrderRow ? getRowLocalFiles(imageOrderRow) : [];

  return (
    <div ref={pageRef}>
      <PageHeader
        eyebrow="Catalog"
        title="Bulk Onboarding"
        subtitle="Fill the table, validate with a dry run, then onboard."
        actions={
          submitted ? (
            <Button variant="outline" onClick={reset}>Onboard more</Button>
          ) : (
            <>
              <Button variant="outline" icon={Download} onClick={downloadTemplate}>
                Download template
              </Button>
              <Button variant="outline" icon={FileUp} onClick={() => fileInputRef.current?.click()} disabled={locked}>
                Upload CSV
              </Button>
              <div className={styles.validateBtnGroup}>
                <Button
                  variant="outline"
                  icon={CheckCircle2}
                  onClick={handleValidateDryRun}
                  disabled={locked || rows.length === 0 || !activeCategories.length}
                >
                  {validating ? 'Validating...' : 'Validate Batch (Dry Run)'}
                </Button>
                <button
                  type="button"
                  className={styles.infoHelpBtn}
                  onClick={() => setShowValidateInfoModal(true)}
                  title="What is Validate Batch?"
                  aria-label="What is Validate Batch?"
                >
                  <HelpCircle size={15} />
                </button>
              </div>
              <Button
                icon={Upload}
                onClick={handleSubmit}
                disabled={locked || !batchValidated || rows.length === 0 || !activeCategories.length}
                title={batchValidated ? undefined : 'Validate Batch (Dry Run) first, then onboard'}
              >
                {submitting
                  ? `Onboarding ${successCount}/${rows.length}…`
                  : `Onboard ${rows.length} product${rows.length !== 1 ? 's' : ''}`}
              </Button>
            </>
          )
        }
      />

      <div className={styles.infoBar}>
        <Info size={15} />
        Fill the table, click Validate Batch (Dry Run), then Onboard. Each row is created, stocked in if quantity is set, then set to Active. Public image URLs are processed directly via bulk import, and local image files are uploaded directly to S3 via presigned URLs.
      </div>

      {submitted && (
        <div className={styles.resultBanner}>
          <CheckCircle2 size={16} />
          <div>
            <p className={styles.resultTitle}>
              {successCount} product{successCount !== 1 ? 's' : ''} onboarded
            </p>
            {errorCount > 0 && (
              <p className={styles.resultSub}>{errorCount} failed — check the status column.</p>
            )}
          </div>
        </div>
      )}

      {validatedNotice && (
        <div className={styles.resultBanner}>
          <CheckCircle2 size={16} />
          <div>
            <p className={styles.resultTitle}>{validatedNotice}</p>
          </div>
        </div>
      )}

      {globalError && (
        <div className={styles.errorBanner}>
          <AlertCircle size={14} />
          {globalError}
        </div>
      )}

      {!activeCategories.length && (
        <div className={styles.errorBanner}>
          <AlertCircle size={14} />
          Add at least one active category on the Products page before bulk onboarding.
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept=".csv,text/csv"
        hidden
        onChange={handleCsvFile}
      />

      {!submitted && (
        <div className={styles.scanSection}>
          <span className={styles.pasteAreaLabel}>Scan to add products</span>
          <div className={styles.scanBox}>
            <ScanBarcode size={20} className={styles.scanIcon} />
            <input
              ref={scanRef}
              type="text"
              value={scanValue}
              onChange={(e) => setScanValue(e.target.value)}
              onKeyDown={handleScanKeyDown}
              placeholder="Scan barcode with HT15, or type EAN and press Enter"
              className={styles.scanInput}
              inputMode="numeric"
              autoComplete="off"
              disabled={locked || !activeCategories.length}
            />
            {scanning ? (
              <Loader2 size={18} className={styles.scanSpinner} aria-hidden />
            ) : scanValue ? (
              <button
                type="button"
                className={styles.clearScan}
                onClick={() => setScanValue('')}
                aria-label="Clear scan field"
              >
                <X size={14} />
              </button>
            ) : null}
          </div>
          {scanNotice && (
            <div className={`${styles.scanNotice} ${styles[`scanNotice${scanNotice.tone.charAt(0).toUpperCase()}${scanNotice.tone.slice(1)}`]}`}>
              {scanNotice.text}
              {scanNotice.itemId && (
                <Link to="/products" className={styles.scanNoticeLink}>View in catalog</Link>
              )}
            </div>
          )}
        </div>
      )}

      {!submitted && (
        <label className={styles.pasteAreaWrap}>
          <span className={styles.pasteAreaLabel}>Paste CSV or Excel rows</span>
          <textarea
            ref={pasteAreaRef}
            className={styles.pasteArea}
            rows={3}
            placeholder="Click here, then press Ctrl+V (or Cmd+V). You can also paste anywhere on this page."
            onPaste={handlePasteAreaPaste}
            onChange={handlePasteAreaChange}
            disabled={locked}
          />
        </label>
      )}

      {!submitted && (
        <div className={styles.skuAutoBar}>
          <div className={styles.skuAutoCopy}>
            <Sparkles size={15} className={styles.skuAutoIcon} />
            <div>
              <p className={styles.skuAutoTitle}>Auto-generate SKUs</p>
              <p className={styles.skuAutoHint}>
                Fills empty SKUs after name, category, and barcode are set, and updates auto SKUs when those fields change.
                Manual SKUs are never overwritten. Turning off does not clear any SKUs.
              </p>
            </div>
          </div>
          <button
            type="button"
            className={`${form.toggle} ${autoGenerateSku ? form.toggleOn : ''}`}
            onClick={handleToggleAutoGenerateSku}
            disabled={locked}
            aria-pressed={autoGenerateSku}
            aria-label={autoGenerateSku ? 'Turn off auto-generate SKUs' : 'Turn on auto-generate SKUs'}
            title={autoGenerateSku ? 'Auto-generate on' : 'Auto-generate off'}
          />
        </div>
      )}

      <div className={styles.tableScroll}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>#</th>
              <th>Image</th>
              <th>Name</th>
              <th>SKU</th>
              <th>Barcode</th>
              <th>Category</th>
              <th>Brand</th>
              <th>Cost</th>
              <th>Selling Price</th>
              <th>MRP</th>
              <th>Stock</th>
              <th>Unit</th>
              <th>Sold as</th>
              <th>GST %</th>
              <th>HSN</th>
              <th>Status</th>
              {!submitted && <th aria-label="remove" />}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, idx) => {
              const result = rowResults[row.id];
              const rowFiles = getRowLocalFiles(row);
              const clashFields = duplicateFields(result);
              return (
                <tr
                  key={row.id}
                  className={result?.status === 'success' ? styles.rowDone : result?.status === 'error' ? styles.rowFailed : ''}
                >
                  <td className={styles.numCell}>{idx + 1}</td>
                  <td className={styles.imageCell}>
                    <div className={styles.imageInputWrap}>
                      {/* Commented out remote image URL input field as requested */}
                      {/* <input
                        className={`${form.input} ${styles.cellInput} ${styles.imageInput}`}
                        placeholder="Image URL"
                        value={row.imageUrl}
                        onChange={(e) => updateRow(row.id, 'imageUrl', e.target.value)}
                        disabled={locked}
                      /> */}
                      {rowFiles.map((file, fileIdx) => (
                        <RowImagePreview
                          key={`${row.id}-${file.name}-${file.size}-${file.lastModified}-${fileIdx}`}
                          file={file}
                          index={fileIdx}
                          totalFiles={rowFiles.length}
                          disabled={locked}
                          onMoveLeft={() => moveRowImage(row.id, fileIdx, fileIdx - 1)}
                          onMoveRight={() => moveRowImage(row.id, fileIdx, fileIdx + 1)}
                          onOpenOrderModal={() => setImageOrderRowId(row.id)}
                          onRemove={() => removeRowImage(row.id, fileIdx)}
                        />
                      ))}
                      {!rowFiles.length && row.imageUrl && (
                        <img
                          src={row.imageUrl}
                          alt=""
                          className={styles.remoteImageThumb}
                          title="Image from barcode lookup"
                        />
                      )}
                      {rowFiles.length < 5 && (
                        <>
                          <label
                            className={styles.fileUploadLabel}
                            title={`Upload local image (${rowFiles.length}/5 max)`}
                          >
                            <ImageIcon size={13} />
                            <input
                              key={`file-input-${row.id}`}
                              type="file"
                              accept="image/*"
                              multiple
                              hidden
                              disabled={locked}
                              onChange={(e) => {
                                const rowId = e.currentTarget.dataset.rowId;
                                if (rowId && e.target.files?.length) {
                                  handleRowFilesChange(rowId, e.target.files);
                                  e.target.value = '';
                                }
                              }}
                              data-row-id={row.id}
                            />
                          </label>
                          <label
                            className={styles.fileUploadLabel}
                            title="Take a photo with the camera"
                          >
                            <Camera size={13} />
                            <input
                              key={`camera-input-${row.id}`}
                              type="file"
                              accept="image/*"
                              capture="environment"
                              hidden
                              disabled={locked}
                              onChange={(e) => {
                                const rowId = e.currentTarget.dataset.rowId;
                                if (rowId && e.target.files?.length) {
                                  handleRowFilesChange(rowId, e.target.files);
                                  e.target.value = '';
                                }
                              }}
                              data-row-id={row.id}
                            />
                          </label>
                        </>
                      )}
                      {rowFiles.length > 0 && (
                        rowFiles.length >= 2 ? (
                          <button
                            type="button"
                            className={styles.imageCountBadge}
                            onClick={() => setImageOrderRowId(row.id)}
                            disabled={locked}
                            title="Click to reorder uploaded images"
                          >
                            <ArrowUpDown size={10} style={{ marginRight: 3, verticalAlign: -1 }} />
                            {rowFiles.length}/5 reorder
                          </button>
                        ) : (
                          <span className={styles.imageCountBadge}>{rowFiles.length}/5</span>
                        )
                      )}
                    </div>
                  </td>
                  <td>
                    <input
                      className={`${form.input} ${styles.cellInput} ${styles.nameInput}`}
                      placeholder="Aashirvaad Atta 5kg"
                      value={row.name}
                      onChange={(e) => updateRow(row.id, 'name', e.target.value)}
                      disabled={locked}
                    />
                  </td>
                  <td>
                    <input
                      className={`${form.input} ${styles.cellInput} ${styles.skuInput} ${clashFields.has('sku') ? styles.cellInvalid : ''}`}
                      placeholder={autoGenerateSku ? 'Needs name, category & barcode' : 'AASH-ATTA-5KG'}
                      value={row.sku}
                      onChange={(e) => updateRow(row.id, 'sku', e.target.value)}
                      disabled={locked}
                      aria-invalid={clashFields.has('sku') || undefined}
                    />
                  </td>
                  <td>
                    <input
                      className={`${form.input} ${styles.cellInput} ${styles.skuInput} ${clashFields.has('barcode') ? styles.cellInvalid : ''}`}
                      placeholder="8901234567890"
                      value={row.barcode}
                      onChange={(e) => updateRow(row.id, 'barcode', e.target.value)}
                      onKeyDown={(e) => handleRowBarcodeKeyDown(e, row.id)}
                      disabled={locked}
                      aria-invalid={clashFields.has('barcode') || undefined}
                    />
                  </td>
                  <td>
                    <select
                      className={`${form.select} ${styles.cellInput} ${styles.catInput}`}
                      value={row.category}
                      onChange={(e) => updateRow(row.id, 'category', e.target.value)}
                      disabled={locked}
                    >
                      {!row.category && <option value="">Select</option>}
                      {activeCategories.map((c) => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <input
                      className={`${form.input} ${styles.cellInput} ${styles.brandInput}`}
                      placeholder="Aashirvaad"
                      value={row.brand}
                      onChange={(e) => updateRow(row.id, 'brand', e.target.value)}
                      disabled={locked}
                    />
                  </td>
                  <td>
                    <input
                      className={`${form.input} ${styles.cellInput} ${styles.numInput}`}
                      type="number"
                      min="0"
                      value={row.costPrice}
                      onChange={(e) => updateRow(row.id, 'costPrice', e.target.value)}
                      disabled={locked}
                    />
                  </td>
                  <td>
                    <input
                      className={`${form.input} ${styles.cellInput} ${styles.numInput}`}
                      type="number"
                      min="0"
                      value={row.price}
                      onChange={(e) => updateRow(row.id, 'price', e.target.value)}
                      disabled={locked}
                    />
                  </td>
                  <td>
                    <input
                      className={`${form.input} ${styles.cellInput} ${styles.numInput}`}
                      type="number"
                      min="0"
                      value={row.mrp}
                      onChange={(e) => updateRow(row.id, 'mrp', e.target.value)}
                      disabled={locked}
                    />
                  </td>
                  <td>
                    <input
                      className={`${form.input} ${styles.cellInput} ${styles.numInput}`}
                      type="number"
                      min="0"
                      value={row.stock}
                      onChange={(e) => updateRow(row.id, 'stock', e.target.value)}
                      disabled={locked}
                    />
                  </td>
                  <td>
                    <select
                      className={`${form.select} ${styles.cellInput} ${styles.unitInput}`}
                      value={row.unit}
                      onChange={(e) => updateRow(row.id, 'unit', e.target.value)}
                      disabled={locked}
                    >
                      {UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
                    </select>
                  </td>
                  <td>
                    <select
                      className={`${form.select} ${styles.cellInput} ${styles.unitInput}`}
                      value={row.soldBy === 'WEIGHT' ? 'WEIGHT' : 'EACH'}
                      onChange={(e) => updateRow(row.id, 'soldBy', e.target.value)}
                      disabled={locked}
                    >
                      <option value="EACH">Pack</option>
                      <option value="WEIGHT">Loose</option>
                    </select>
                  </td>
                  <td>
                    <select
                      className={`${form.select} ${styles.cellInput} ${styles.gstInput}`}
                      value={normalizeGstRate(row.gstRate)}
                      onChange={(e) => updateRow(row.id, 'gstRate', Number(e.target.value))}
                      disabled={locked}
                    >
                      {GST_RATES.map((r) => (
                        <option key={r} value={r}>{r}%</option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <input
                      className={`${form.input} ${styles.cellInput} ${styles.hsnInput}`}
                      placeholder="1006"
                      value={row.hsnCode}
                      onChange={(e) => updateRow(row.id, 'hsnCode', e.target.value)}
                      disabled={locked}
                    />
                  </td>
                  <td className={styles.statusCell}>
                    <RowStatus result={result} />
                    {result?.status === 'error' && result.error && (
                      <p className={styles.rowErrorText}>
                        {formatBulkError(result.error)}
                      </p>
                    )}
                  </td>
                  {!submitted && (
                    <td className={styles.removeCell}>
                      <button
                        type="button"
                        className={styles.removeBtn}
                        onClick={() => removeRow(row.id)}
                        disabled={rows.length === 1 || locked}
                        aria-label="Remove row"
                      >
                        <Trash2 size={13} />
                      </button>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {!submitted && (
        <button type="button" className={styles.addRowBtn} onClick={addRow} disabled={locked}>
          <Plus size={14} />
          Add another row
        </button>
      )}

      {!submitted && (
        <div className={styles.csvGuide}>
          <div className={styles.csvGuideHead}>
            <p className={styles.csvTitle}>CSV / paste format</p>
            <button type="button" className={styles.csvDownloadLink} onClick={downloadTemplate}>
              <Download size={13} />
              Download template
            </button>
          </div>
          <code className={styles.csvExample}>{CSV_HEADERS.join(', ')}</code>
          <code className={styles.csvExample}>Aashirvaad Atta 5kg, AASH-ATTA-5KG, 8901234567890, Staples, Aashirvaad, 220, 265, 280, 40, ea, 5, 1006, https://images.unsplash.com/photo-1586201375761-83865001e31c</code>
        </div>
      )}

      {imageOrderRow && imageOrderFiles.length >= 2 && (
        <ImageOrderModal
          rowLabel={imageOrderRow.name || imageOrderRow.sku}
          files={imageOrderFiles}
          disabled={locked}
          onClose={() => setImageOrderRowId(null)}
          onSave={(orderedFiles) => reorderRowImages(imageOrderRow.id, orderedFiles)}
        />
      )}

      {showValidateInfoModal && (
        <div className={styles.modalOverlay} onClick={() => setShowValidateInfoModal(false)}>
          <div className={styles.modalCard} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <div className={styles.modalTitleWrap}>
                <Info className={styles.modalTitleIcon} size={18} />
                <h3 className={styles.modalTitle}>What is "Validate Batch"?</h3>
              </div>
              <button
                type="button"
                className={styles.modalCloseBtn}
                onClick={() => setShowValidateInfoModal(false)}
                aria-label="Close modal"
              >
                <X size={16} />
              </button>
            </div>
            <div className={styles.modalBody}>
              <p>
                <strong>Validate Batch (Dry Run)</strong> is required before onboarding. It runs the same field checks as real import on the backend, without writing products, stock, or images.
              </p>
              <div className={styles.modalFeatureList}>
                <div className={styles.modalFeatureItem}>
                  <CheckCircle2 size={16} className={styles.modalCheckIcon} />
                  <div>
                    <strong>Backend field checks</strong>
                    <p>Validates name, category, HSN (4–8 digits), GST (0 / 5 / 18 / 40), and sold as (Pack / Loose). Statuses like would_create / would_update — no DB writes.</p>
                  </div>
                </div>
                <div className={styles.modalFeatureItem}>
                  <CheckCircle2 size={16} className={styles.modalCheckIcon} />
                  <div>
                    <strong>In-batch SKU & barcode clashes</strong>
                    <p>Caught in the app before dry-run. Checks in-batch duplicates and validates barcodes against the catalog via direct registry lookup. Empty barcodes are allowed.</p>
                  </div>
                </div>
                <div className={styles.modalFeatureItem}>
                  <CheckCircle2 size={16} className={styles.modalCheckIcon} />
                  <div>
                    <strong>Row-level error highlighting</strong>
                    <p>Shows row-specific messages so you can fix issues before the real onboard.</p>
                  </div>
                </div>
                <div className={styles.modalFeatureItem}>
                  <CheckCircle2 size={16} className={styles.modalCheckIcon} />
                  <div>
                    <strong>Safe testing</strong>
                    <p>Dry-run does not create products, stock-in, or fetch remote images.</p>
                  </div>
                </div>
              </div>
            </div>
            <div className={styles.modalFooter}>
              <Button variant="outline" onClick={() => setShowValidateInfoModal(false)}>
                Got it
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
